import React, { useState, useEffect, useCallback } from 'react';
import {
  getAdmins,
  createAdmin,
  changeMyPassword,
  getRoleLabel,
} from '../services/accountService';
import { getAdminData } from '../services/authService';
import { formatDate } from '../utils/dateFormat';
import Header from '../components/common/Header';
import PageHeader from '../components/common/PageHeader';
import Snackbar from '../components/common/Snackbar';
import useSnackbar from '../hooks/useSnackbar';
import '../css/AccountManagement.css';

const initialNewAdmin = {
  email: '',
  password: '',
  adminName: '',
  role: 'GENERAL',
};

const initialPwForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const AccountManagement = () => {
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [me, setMe] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState(initialNewAdmin);
  const [submitting, setSubmitting] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState(initialPwForm);
  const [pwSubmitting, setPwSubmitting] = useState(false);

  const isCeo = me?.role === 'CEO';

  useEffect(() => {
    setMe(getAdminData());
  }, []);

  const fetchAdmins = useCallback(async () => {
    if (!isCeo) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await getAdmins();
      setAdmins(result?.data || []);
    } catch (error) {
      showSnackbar(error.message || '관리자 목록을 불러오지 못했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [isCeo, showSnackbar]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!newAdmin.email.trim() || !newAdmin.password || !newAdmin.adminName.trim()) {
      showSnackbar('모든 필드를 입력해주세요.', 'error');
      return;
    }
    if (newAdmin.password.length < 4) {
      showSnackbar('비밀번호는 4자 이상이어야 합니다.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await createAdmin(newAdmin);
      showSnackbar(`관리자 [${newAdmin.email}] 등록 완료`, 'success');
      setNewAdmin(initialNewAdmin);
      setCreateOpen(false);
      fetchAdmins();
    } catch (error) {
      showSnackbar(error.message || '관리자 등록에 실패했습니다.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwSubmitting) return;
    if (!pwForm.currentPassword || !pwForm.newPassword) {
      showSnackbar('현재 비밀번호와 새 비밀번호를 입력해주세요.', 'error');
      return;
    }
    if (pwForm.newPassword.length < 4) {
      showSnackbar('새 비밀번호는 4자 이상이어야 합니다.', 'error');
      return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      showSnackbar('새 비밀번호 확인이 일치하지 않습니다.', 'error');
      return;
    }
    setPwSubmitting(true);
    try {
      await changeMyPassword(pwForm.currentPassword, pwForm.newPassword);
      showSnackbar('비밀번호가 변경되었습니다.', 'success');
      setPwForm(initialPwForm);
      setPwOpen(false);
    } catch (error) {
      showSnackbar(error.message || '비밀번호 변경에 실패했습니다.', 'error');
    } finally {
      setPwSubmitting(false);
    }
  };

  return (
    <div className="admin-layout">
      <Header />
      <PageHeader title="계정 관리" />
      <div className="account-management">
        <div className="main-content">

          <section className="card my-account-card">
            <div className="card-header">
              <h2>내 계정</h2>
              <button className="btn-primary" onClick={() => setPwOpen(true)}>비밀번호 변경</button>
            </div>
            <dl className="info-list">
              <div><dt>이름</dt><dd>{me?.name || '-'}</dd></div>
              <div><dt>이메일</dt><dd>{me?.email || '-'}</dd></div>
              <div><dt>역할</dt><dd><span className="role-badge">{getRoleLabel(me?.role)}</span></dd></div>
            </dl>
          </section>

          {isCeo && (
            <section className="card">
              <div className="card-header">
                <h2>관리자 계정 목록</h2>
                <button className="btn-primary" onClick={() => setCreateOpen(true)}>관리자 추가</button>
              </div>
              <div className="table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>이메일</th>
                      <th>이름</th>
                      <th>역할</th>
                      <th>등록일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={5} className="empty-row">불러오는 중...</td></tr>
                    ) : admins.length === 0 ? (
                      <tr><td colSpan={5} className="empty-row">등록된 관리자가 없습니다.</td></tr>
                    ) : (
                      admins.map((a) => (
                        <tr key={a.id}>
                          <td>{a.id}</td>
                          <td>{a.email}</td>
                          <td>{a.adminName}</td>
                          <td><span className="role-badge">{getRoleLabel(a.role)}</span></td>
                          <td>{formatDate(a.createdAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {!isCeo && (
            <section className="card">
              <p className="permission-notice">관리자 목록은 CEO 권한에서만 조회할 수 있습니다.</p>
            </section>
          )}
        </div>
      </div>

      {createOpen && (
        <div className="modal-overlay" onClick={() => !submitting && setCreateOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>관리자 추가</h2>
              <button className="modal-close" onClick={() => !submitting && setCreateOpen(false)}>×</button>
            </div>
            <form className="modal-form" onSubmit={handleCreate}>
              <label>이메일 *
                <input type="email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} required disabled={submitting} />
              </label>
              <label>비밀번호 *
                <input type="password" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} placeholder="4자 이상" required disabled={submitting} />
              </label>
              <label>이름 *
                <input type="text" value={newAdmin.adminName} onChange={(e) => setNewAdmin({ ...newAdmin, adminName: e.target.value })} required disabled={submitting} />
              </label>
              <label>역할 *
                <select value={newAdmin.role} onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })} disabled={submitting}>
                  <option value="GENERAL">일반 관리자</option>
                  <option value="CEO">대표</option>
                </select>
              </label>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setCreateOpen(false)} disabled={submitting}>취소</button>
                <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? '등록중...' : '등록'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pwOpen && (
        <div className="modal-overlay" onClick={() => !pwSubmitting && setPwOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>비밀번호 변경</h2>
              <button className="modal-close" onClick={() => !pwSubmitting && setPwOpen(false)}>×</button>
            </div>
            <form className="modal-form" onSubmit={handleChangePassword}>
              <label>현재 비밀번호 *
                <input type="password" value={pwForm.currentPassword} onChange={(e) => setPwForm({ ...pwForm, currentPassword: e.target.value })} required disabled={pwSubmitting} />
              </label>
              <label>새 비밀번호 *
                <input type="password" value={pwForm.newPassword} onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })} placeholder="4자 이상" required disabled={pwSubmitting} />
              </label>
              <label>새 비밀번호 확인 *
                <input type="password" value={pwForm.confirmPassword} onChange={(e) => setPwForm({ ...pwForm, confirmPassword: e.target.value })} required disabled={pwSubmitting} />
              </label>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setPwOpen(false)} disabled={pwSubmitting}>취소</button>
                <button type="submit" className="btn-primary" disabled={pwSubmitting}>{pwSubmitting ? '변경중...' : '변경'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Snackbar message={snackbar.message} type={snackbar.type} isOpen={snackbar.isOpen} onClose={hideSnackbar} />
    </div>
  );
};

export default AccountManagement;